/**
 * Storefront traffic preflight.
 *
 * This is client-side compatibility screening, not an edge/WAF control. It
 * records a reason code, avoids persistent query/cookie bypasses, and keeps
 * Shopify development surfaces reachable for implementation and QA.
 */
(() => {
  'use strict';

  const DEFAULTS = Object.freeze({
    enabled: true,
    redirectPath: '/pages/not-found',
    persistenceMs: 5 * 60 * 1000,
    analyticsEnabled: false,
    analyticsMeasurementId: '',
    analyticsEventName: 'ecom_traffic_blocked',
    analyticsMaxAgeMs: 10 * 60 * 1000,
    pageType: '',
    debug: false,
    enableSpyReferrerBlock: true,
    enableScannerUserAgentBlock: true,
    enableAutomationBlock: true,
    hardBlockSelectedTimezones: true,
    hardBlockSelectedLanguages: false,
    forbiddenReferrerFragments: [],
    forbiddenUserAgentFragments: [],
    allowedCrawlerFragments: [],
    blockedLanguages: [],
    excludedRegions: [],
    blockedTimezones: [],
    excludedTimezones: [],
  });
  const config = Object.assign({}, DEFAULTS, window.themeBlockConfig || {});
  const browserLocation = window.location;
  const browserNavigator = window.navigator || navigator;

  const normalizeList = (value, fallback) => {
    if (Array.isArray(value)) return value.map(String).map((item) => item.trim()).filter(Boolean);
    if (typeof value === 'string') return value.split(',').map((item) => item.trim()).filter(Boolean);
    return [...fallback];
  };

  const normalizeRedirectPath = (value) => {
    try {
      const target = new URL(String(value || DEFAULTS.redirectPath), browserLocation.origin);
      return target.origin === browserLocation.origin && target.pathname
        ? target.pathname
        : DEFAULTS.redirectPath;
    } catch (_) {
      return DEFAULTS.redirectPath;
    }
  };

  const redirectPath = normalizeRedirectPath(config.redirectPath);
  const persistenceMs = Math.max(0, Number(config.persistenceMs) || 0);
  const sessionKey = `theme_block_decision:${redirectPath}`;
  const analyticsStorageKey = `${sessionKey}:analytics`;
  const toLowerCaseList = (value, fallback) => normalizeList(value, fallback).map((item) => item.toLowerCase());
  const forbiddenReferrers = toLowerCaseList(config.forbiddenReferrerFragments, DEFAULTS.forbiddenReferrerFragments);
  const forbiddenUserAgents = toLowerCaseList(config.forbiddenUserAgentFragments, DEFAULTS.forbiddenUserAgentFragments);
  const allowedCrawlers = toLowerCaseList(config.allowedCrawlerFragments, DEFAULTS.allowedCrawlerFragments);
  const blockedLanguages = toLowerCaseList(config.blockedLanguages, DEFAULTS.blockedLanguages);
  const excludedRegions = toLowerCaseList(config.excludedRegions, DEFAULTS.excludedRegions);
  const blockedTimezones = new Set(normalizeList(config.blockedTimezones, DEFAULTS.blockedTimezones));
  const excludedTimezones = new Set(normalizeList(config.excludedTimezones, DEFAULTS.excludedTimezones));

  const publish = (decision) => {
    window.__THEME_BLOCK_DECISION__ = decision;
    if (config.debug) console.info('[Theme block preflight]', decision);
  };

  const allow = (reason, signals = {}) => {
    publish({ action: 'allow', reason, signals });
  };

  const containsAny = (value, fragments) => fragments.some((fragment) => value.includes(fragment));

  const getTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (_) {
      return '';
    }
  };

  const getLanguages = () => {
    const values = Array.isArray(browserNavigator.languages)
      ? browserNavigator.languages
      : [browserNavigator.language || ''];
    return values.map((value) => String(value).toLowerCase());
  };

  const isBlockedLanguage = (languages) =>
    languages.some(
      (language) =>
        !excludedRegions.some((region) => language === region || language.startsWith(`${region}-`)) &&
        blockedLanguages.some((code) => language === code || language.startsWith(`${code}-`))
    );

  const normalizeAnalyticsPath = (value) => {
    try {
      const url = new URL(String(value || ''), browserLocation.origin);
      if (url.origin !== browserLocation.origin) return '/';
      const path = url.pathname || '/';
      if (/^\/(account|checkouts?|orders?)(?:\/|$)/i.test(path)) {
        return `/${path.split('/')[1].toLowerCase()}`;
      }
      return path.slice(0, 120);
    } catch (_) {
      return '/';
    }
  };

  const getQueryTags = () => {
    try {
      const search = new URLSearchParams(browserLocation.search || '');
      return ['srsltid', 'utm_source', 'utm_medium', 'gclid', 'fbclid']
        .filter((key) => search.has(key))
        .join(',') || 'none';
    } catch (_) {
      return 'none';
    }
  };

  const getReferrerType = (value) => {
    const raw = String(value || '').toLowerCase();
    if (!raw) return 'none';
    if (containsAny(raw, forbiddenReferrers)) return 'known-spy';
    try {
      const url = new URL(raw, browserLocation.origin);
      if (url.origin === browserLocation.origin) return 'internal';
      const hostname = url.hostname.toLowerCase();
      if (hostname.includes('google.')) return 'google';
      if (hostname.includes('bing.')) return 'bing';
      if (containsAny(hostname, ['facebook.', 'instagram.', 'tiktok.', 'pinterest.', 'reddit.', 'youtube.'])) {
        return 'social';
      }
      return 'external';
    } catch (_) {
      return 'invalid';
    }
  };

  const getBrowserDetails = (userAgent) => {
    const value = String(userAgent || '').toLowerCase();
    const matchers = [
      { name: 'edge', pattern: /edg(?:e|a|ios)?\/(\d+)/ },
      { name: 'opera', pattern: /opr\/(\d+)/ },
      { name: 'firefox', pattern: /firefox\/(\d+)/ },
      { name: 'chrome', pattern: /(?:chrome|crios)\/(\d+)/ },
      { name: 'safari', pattern: /version\/(\d+).*safari\// },
    ];
    const match = matchers.map((item) => ({ ...item, version: value.match(item.pattern) })).find((item) => item.version);
    if (value.includes('android') && (/\bwv\)/.test(value) || /version\/\d+.*chrome/.test(value))) {
      const webViewVersion = value.match(/version\/(\d+)/);
      return { name: 'android-webview', major: webViewVersion ? webViewVersion[1] : 'unknown' };
    }
    return match ? { name: match.name, major: match.version[1] } : { name: 'unknown', major: 'unknown' };
  };

  const getOsFamily = (userAgent) => {
    const value = String(userAgent || '').toLowerCase();
    if (value.includes('cros')) return 'chromeos';
    if (value.includes('windows')) return 'windows';
    if (value.includes('android')) return 'android';
    if (value.includes('iphone') || value.includes('ipad') || value.includes('ipod')) return 'ios';
    if (value.includes('mac os x')) return 'macintosh';
    if (value.includes('linux')) return 'linux';
    return 'unknown';
  };

  const getDeviceCategory = (userAgent) => {
    const value = String(userAgent || '').toLowerCase();
    if (value.includes('ipad') || (value.includes('android') && !value.includes('mobile'))) return 'tablet';
    if (value.includes('mobile') || value.includes('iphone') || value.includes('ipod') || value.includes('android')) {
      return 'mobile';
    }
    return 'desktop';
  };

  const getPrimaryLanguage = (languages) => {
    const value = Array.isArray(languages) && languages.length ? String(languages[0] || '') : '';
    return value.toLowerCase().split('-')[0].slice(0, 12) || 'unknown';
  };

  const getSignalSummary = (signals) =>
    [
      ['spy-referrer', signals.knownSpyReferrer],
      ['scanner-ua', signals.knownScannerUserAgent],
      ['webdriver', signals.webdriver],
      ['language', signals.selectedLanguage],
      ['timezone', signals.selectedTimezone],
    ]
      .filter(([, matched]) => matched)
      .map(([name]) => name)
      .join(',') || 'none';

  const readPersistedDecision = () => {
    try {
      const value = JSON.parse(window.sessionStorage.getItem(sessionKey) || 'null');
      if (!value || value.action !== 'block' || !value.timestamp) return null;
      if (Date.now() - value.timestamp >= persistenceMs) {
        window.sessionStorage.removeItem(sessionKey);
        return null;
      }
      return value;
    } catch (_) {
      return null;
    }
  };

  const persistDecision = (decision) => {
    try {
      window.sessionStorage.setItem(sessionKey, JSON.stringify({ ...decision, timestamp: Date.now() }));
    } catch (_) {
      // Session storage only shortens repeat checks; it is not a security boundary.
    }
  };

  const analyticsAllowed = () => {
    try {
      const checker = window.Shopify && window.Shopify.customerPrivacy
        ? window.Shopify.customerPrivacy.analyticsProcessingAllowed
        : null;
      return typeof checker === 'function' ? checker() !== false : true;
    } catch (_) {
      return true;
    }
  };

  const buildAnalyticsPayload = (decision) => {
    const userAgent = String(browserNavigator.userAgent || '');
    const browser = getBrowserDetails(userAgent);
    const entryPath = normalizeAnalyticsPath(browserLocation.href);
    return {
      ecom_block_version: 'v2',
      ecom_block_reason: String(decision.reason || 'unknown').slice(0, 40),
      ecom_block_signals: getSignalSummary(decision.signals || {}),
      ecom_block_entry_path: entryPath,
      ecom_page_type: String(config.pageType || 'unknown').slice(0, 40),
      ecom_block_timezone: getTimezone() || 'unknown',
      ecom_block_language: getPrimaryLanguage(getLanguages()),
      ecom_block_referrer_type: getReferrerType(document.referrer),
      ecom_block_browser: browser.name,
      ecom_block_browser_major: browser.major,
      ecom_block_os: getOsFamily(userAgent),
      ecom_block_device: getDeviceCategory(userAgent),
      ecom_block_webdriver: browserNavigator.webdriver === true ? 'true' : 'false',
      ecom_block_query_tags: getQueryTags(),
    };
  };

  const queueAnalytics = (decision) => {
    if (!config.analyticsEnabled || !config.analyticsMeasurementId) return;
    try {
      window.sessionStorage.setItem(
        analyticsStorageKey,
        JSON.stringify({ timestamp: Date.now(), payload: buildAnalyticsPayload(decision) })
      );
    } catch (_) {
      // Telemetry is optional and must never weaken the redirect behavior.
    }
  };

  const readPendingAnalytics = () => {
    try {
      const value = JSON.parse(window.sessionStorage.getItem(analyticsStorageKey) || 'null');
      if (!value || !value.timestamp || Date.now() - value.timestamp > Number(config.analyticsMaxAgeMs)) {
        window.sessionStorage.removeItem(analyticsStorageKey);
        return null;
      }
      return value;
    } catch (_) {
      return null;
    }
  };

  const reportPendingAnalytics = () => {
    const pending = readPendingAnalytics();
    if (!pending) return;

    if (!analyticsAllowed()) {
      window.sessionStorage.removeItem(analyticsStorageKey);
      return;
    }

    const measurementId = String(config.analyticsMeasurementId || '');
    if (!config.analyticsEnabled || !measurementId) {
      window.sessionStorage.removeItem(analyticsStorageKey);
      return;
    }

    window.dataLayer = window.dataLayer || [];
    const hadGtag = typeof window.gtag === 'function';
    const gtag = window.gtag || function() {
      window.dataLayer.push(arguments);
    };
    window.gtag = gtag;

    if (!hadGtag) {
      gtag('js', new Date());
      gtag('config', measurementId, {
        send_page_view: false,
        allow_ad_personalization_signals: false,
      });
    }
    gtag('event', String(config.analyticsEventName || 'ecom_traffic_blocked'), {
      send_to: measurementId,
      ...pending.payload,
    });

    if (!document.querySelector('script[data-vep-block-ga4], script[src*="googletagmanager.com/gtag/js"]')) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
      script.dataset.vepBlockGa4 = 'true';
      (document.head || document.documentElement).appendChild(script);
    }

    window.sessionStorage.removeItem(analyticsStorageKey);
  };

  const redirect = (decision) => {
    persistDecision(decision);
    queueAnalytics(decision);
    publish(decision);
    browserLocation.replace(redirectPath);
  };

  if (!config.enabled) {
    allow('disabled');
    return;
  }

  if (window.Shopify && window.Shopify.designMode) {
    allow('theme-editor');
    return;
  }

  const hostname = String(browserLocation.hostname || '').toLowerCase();
  const isLocalDevelopment = ['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]'].includes(hostname);
  if (isLocalDevelopment) {
    allow('local-development');
    return;
  }

  const search = new URLSearchParams(browserLocation.search || '');
  if (search.has('preview_theme_id') || search.has('preview_theme')) {
    allow('development-preview');
    return;
  }

  if (browserLocation.pathname === redirectPath) {
    reportPendingAnalytics();
    allow('redirect-target');
    return;
  }

  const persisted = readPersistedDecision();
  if (persisted) {
    redirect({
      ...persisted,
      reason: `persisted-${persisted.reason || 'block'}`,
      signals: persisted.signals || {},
    });
    return;
  }

  const userAgent = String(browserNavigator.userAgent || '').toLowerCase();
  const referrer = String(document.referrer || '').toLowerCase();
  const languages = getLanguages();
  const timezone = getTimezone();

  const signals = {
    knownSpyReferrer: containsAny(referrer, forbiddenReferrers),
    knownScannerUserAgent: containsAny(userAgent, forbiddenUserAgents),
    webdriver: browserNavigator.webdriver === true,
    selectedLanguage: isBlockedLanguage(languages),
    selectedTimezone:
      Boolean(timezone) && !excludedTimezones.has(timezone) && blockedTimezones.has(timezone),
  };

  if (containsAny(userAgent, allowedCrawlers)) {
    allow('allowed-crawler', signals);
    return;
  }

  let decision = { action: 'allow', reason: 'none', signals };
  if (config.enableSpyReferrerBlock && signals.knownSpyReferrer) {
    decision = { action: 'block', reason: 'known-spy-referrer', signals };
  } else if (config.enableScannerUserAgentBlock && signals.knownScannerUserAgent) {
    decision = { action: 'block', reason: 'known-scanner-user-agent', signals };
  } else if (config.hardBlockSelectedTimezones && signals.selectedTimezone) {
    decision = { action: 'block', reason: 'selected-timezone', signals };
  } else if (config.hardBlockSelectedLanguages && signals.selectedLanguage) {
    decision = { action: 'block', reason: 'selected-language', signals };
  } else if (config.enableAutomationBlock && signals.webdriver && (signals.selectedTimezone || signals.selectedLanguage)) {
    decision = { action: 'block', reason: 'automation-plus-region-signal', signals };
  }

  if (decision.action === 'block') {
    redirect(decision);
  } else {
    publish(decision);
  }
})();
